import React, { useCallback, useMemo, useState } from 'react';
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Switch,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  AccountCircle as AccountIcon,
  AdminPanelSettings as AdminIcon,
  Bolt as QuickActionsIcon,
  Business as BusinessIcon,
  DarkMode as DarkModeIcon,
  HelpOutline as HelpIcon,
  LightMode as LightModeIcon,
  Logout as LogoutIcon,
  Notifications as NotificationsIcon,
  PersonAdd as RegisterIcon,
  PrivacyTip as PrivacyIcon,
  Settings as SettingsIcon,
  Summarize as ReportIcon,
  UploadFile as UploadIcon,
  Forest as OffsetsIcon,
  GppGood as TermsIcon,
  InfoOutlined as AboutInfoIcon,
  AutoAwesome as WhyIcon,
  HandshakeOutlined as PartnersIcon,
  Article as InsightsIcon,
  CurrencyRupee as PricingIcon,
  Menu as MenuIcon,
  MenuOpen as MenuOpenIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useRegistration } from '../context/RegistrationContext';
import { useMobileNav } from '../context/MobileNavContext';
import { useThemeMode } from '../context/ThemeModeContext';
import HeaderBreadcrumbs from './HeaderBreadcrumbs';
import BrandLockup from './BrandLockup';
import { isPublicRoute, shouldShowAppSidebar, isInternalPortalPath } from '../constants/appNavigation';
import { headerMainColumnInsetSx } from '../constants/appShellLayout';
import { useHeaderWorkspace } from '../hooks/useHeaderWorkspace';
import { useMsmeSetupProgress } from '../hooks/useMsmeSetupProgress';
import { getCurrentUserRole, isAdminPortalUser, isAdminUserProfile, isViewRoleProfile, isSupportPortalUser, isExpertPortalUser } from '../utils/authRole';
import { isBillUploadEnabled } from '../constants/uiVisibility';

const routePathname = (path: string): string => path.split(/[?#]/)[0];

const BASE_QUICK_ACTIONS = [
  { label: 'Upload bills', path: '/document-upload', icon: <UploadIcon fontSize="small" /> },
  { label: 'Offsets and credits', path: '/carbon-savings?section=trading', icon: <OffsetsIcon fontSize="small" /> },
  { label: 'Generate report', path: '/reporting', icon: <ReportIcon fontSize="small" /> },
  { label: 'Help center', path: '/help', icon: <HelpIcon fontSize="small" /> },
] as const;

interface HeaderProps {
  desktopSidebarCollapsed?: boolean;
  onExpandDesktopSidebar?: () => void;
}

const Header: React.FC<HeaderProps> = ({
  desktopSidebarCollapsed = false,
  onExpandDesktopSidebar,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isDesktopNav = useMediaQuery(theme.breakpoints.up('md'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [quickMenuAnchor, setQuickMenuAnchor] = useState<null | HTMLElement>(null);
  const [notificationsAnchor, setNotificationsAnchor] = useState<null | HTMLElement>(null);
  const { isRegistered, registrationData, logout } = useRegistration();
  const { toggleMobileNav } = useMobileNav();
  const { isDarkMode, toggleThemeMode } = useThemeMode();
  const isAdmin = isAdminUserProfile();
  const isReviewer = isViewRoleProfile();
  const hasPortalAccess = isAdminPortalUser();
  const isPartner = getCurrentUserRole() === 'partner';
  const isSupport = isSupportPortalUser();
  const isExpert = isExpertPortalUser();
  // MSME-only workspace APIs (/msme/stats, /msme/profile) must not run for enterprise/admin/support/partner.
  const enableMsmeWorkspace = isRegistered
    && getCurrentUserRole() === 'msme'
    && !hasPortalAccess
    && !isPartner
    && !isSupport;
  const workspace = useHeaderWorkspace(enableMsmeWorkspace);
  const msmeSetupProgress = useMsmeSetupProgress(enableMsmeWorkspace);
  const hideHeaderChrome = isInternalPortalPath(location.pathname);
  const authenticatedHomePath = isPartner
    ? '/partner/dashboard'
    : isExpert
      ? '/expert/dashboard'
      : isSupport
        ? '/support/dashboard'
        : hasPortalAccess
          ? '/admin/dashboard'
          : '/dashboard';
  const publicRegisterHref = new URLSearchParams(location.search).get('type') === 'partner'
    || location.pathname.startsWith('/partners')
    ? '/register?type=partner'
    : '/register?type=msme';

  const showBreadcrumbs = isRegistered && shouldShowAppSidebar(location.pathname, isRegistered);
  const isDevEnvironment = process.env.NODE_ENV === 'development';

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNavigation = useCallback(
    (path: string) => {
      if (!isRegistered && !isPublicRoute(routePathname(path))) {
        navigate('/');
        handleMenuClose();
        setQuickMenuAnchor(null);
        setNotificationsAnchor(null);
        return;
      }

      navigate(path);
      handleMenuClose();
      setQuickMenuAnchor(null);
      setNotificationsAnchor(null);
    },
    [isRegistered, navigate]
  );

  const handleAboutUsNavigation = useCallback(() => {
    handleNavigation('/about-us');
  }, [handleNavigation]);

  const handleAuthAction = () => {
    handleMenuClose();

    if (isRegistered) {
      logout({ redirectTo: '/' });
      return;
    }

    navigate('/login');
  };

  const quickActions = useMemo(() => {
    const visibleActions = <T extends { path: string }>(actions: T[]): T[] => (
      isBillUploadEnabled()
        ? actions
        : actions.filter((action) => !action.path.startsWith('/document-upload'))
    );

    if (hasPortalAccess || isPartner) {
      return visibleActions([...BASE_QUICK_ACTIONS]);
    }

    if (!msmeSetupProgress.myCompanyComplete) {
      return visibleActions([
        {
          label: 'Finish My company',
          path: msmeSetupProgress.nextMyCompanyPath || '/msme-profile',
          icon: <AccountIcon fontSize="small" />,
        },
        { label: 'Help center', path: '/help', icon: <HelpIcon fontSize="small" /> },
      ]);
    }

    if (msmeSetupProgress.progress.allComplete) {
      return visibleActions([...BASE_QUICK_ACTIONS]);
    }

    const onboardingActions: Array<{ label: string; path: string; icon: React.ReactNode }> = [];
    const profileStep = msmeSetupProgress.progress.steps.find((step) => step.id === 'profile');
    const documentsStep = msmeSetupProgress.progress.steps.find((step) => step.id === 'documents');

    if (profileStep && !profileStep.complete) {
      onboardingActions.push({
        label: 'Complete profile',
        path: '/msme-profile',
        icon: <AccountIcon fontSize="small" />,
      });
    }
    if (isBillUploadEnabled() && documentsStep && !documentsStep.complete) {
      onboardingActions.push({
        label: 'Upload bill',
        path: '/document-upload',
        icon: <UploadIcon fontSize="small" />,
      });
    }

    return visibleActions([
      ...onboardingActions,
      ...BASE_QUICK_ACTIONS.filter((action) => !onboardingActions.some((item) => item.path === action.path)),
    ]);
  }, [
    hasPortalAccess,
    isPartner,
    msmeSetupProgress.progress,
    msmeSetupProgress.myCompanyComplete,
    msmeSetupProgress.nextMyCompanyPath,
  ]);

  const mode = theme.palette.mode;
  const companyName = registrationData?.companyName?.trim();

  if (hideHeaderChrome) {
    return null;
  }

  return (
    <AppBar
      position="sticky"
      elevation={0}
      color="inherit"
      sx={{
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        backdropFilter: 'blur(14px)',
        backgroundImage:
          mode === 'light'
            ? 'linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.78) 100%)'
            : 'linear-gradient(180deg, rgba(26,34,44,0.96) 0%, rgba(26,34,44,0.88) 100%)',
      }}
    >
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: 'absolute',
          left: -9999,
          top: 'auto',
          width: 1,
          height: 1,
          overflow: 'hidden',
          '&:focus': {
            position: 'fixed',
            left: 16,
            top: 8,
            zIndex: theme.zIndex.tooltip + 1,
            width: 'auto',
            height: 'auto',
            overflow: 'visible',
            px: 2,
            py: 1,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            borderRadius: 1,
            textDecoration: 'none',
            fontWeight: 600,
          },
        }}
      >
        Skip to main content
      </Box>

      <Toolbar sx={{ py: 0.75, minHeight: { xs: 56, md: 64 }, gap: 1, flexWrap: { xs: 'wrap', lg: 'nowrap' } }}>
        {showBreadcrumbs && (
          <IconButton
            aria-label="open navigation menu"
            onClick={toggleMobileNav}
            sx={{ display: { xs: 'inline-flex', md: 'none' }, mr: 0.25, flexShrink: 0 }}
          >
            <MenuIcon />
          </IconButton>
        )}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            mr: { xs: 0, sm: 'auto' },
            minWidth: 0,
            flex: { xs: '1 1 auto', sm: '0 1 auto' },
          }}
          onClick={() => handleNavigation(isRegistered ? authenticatedHomePath : '/')}
        >
          <BrandLockup
            variant="compact"
            logoWidth={{ xs: 48, sm: 56, md: 64 }}
            showTagline
            showVersion
            sx={{ mr: 0.5, flexShrink: 0 }}
          />
          {isDevEnvironment && (
            <Chip
              size="small"
              label="DEV"
              color="warning"
              sx={{ height: 20, alignSelf: 'center', display: { xs: 'none', md: 'inline-flex' } }}
            />
          )}
        </Box>

        {isRegistered && companyName && (
          <Typography
            variant="body2"
            noWrap
            sx={{
              display: { xs: 'none', md: 'block' },
              maxWidth: { md: 200, lg: 280 },
              fontWeight: 600,
              color: 'text.secondary',
              order: { xs: 3, lg: 0 },
              mx: { lg: 2 },
            }}
          >
            {companyName}
          </Typography>
        )}

        {!isRegistered && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1, ml: 'auto' }}>
            <Button
              color="inherit"
              size="small"
              onClick={handleAboutUsNavigation}
              sx={{ display: { xs: 'none', md: 'inline-flex' } }}
            >
              About us
            </Button>
            <Button
              color="inherit"
              size="small"
              onClick={() => handleNavigation('/why-carbon-intelligence')}
              sx={{ display: { xs: 'none', lg: 'inline-flex' } }}
            >
              Why Carbon Intelligence?
            </Button>
            <Button
              color="inherit"
              size="small"
              onClick={() => handleNavigation('/pricing')}
              sx={{ display: { xs: 'none', md: 'inline-flex' } }}
            >
              Pricing
            </Button>
            <Button
              color="inherit"
              size="small"
              onClick={() => handleNavigation('/insights')}
              sx={{ display: { xs: 'none', lg: 'inline-flex' } }}
            >
              Insights
            </Button>
            <Button
              color="inherit"
              size="small"
              onClick={() => handleNavigation('/partners')}
              sx={{ display: { xs: 'none', lg: 'inline-flex' } }}
            >
              Partners
            </Button>
            <Button
              color="inherit"
              size="small"
              onClick={() => handleNavigation('/help')}
              sx={{ display: { xs: 'none', xl: 'inline-flex' } }}
            >
              Help
            </Button>
            <Button
              color="inherit"
              size="small"
              onClick={() => handleNavigation('/privacy-policy')}
              sx={{ display: { xs: 'none', xl: 'inline-flex' } }}
            >
              Privacy
            </Button>
            <Button
              color="inherit"
              size="small"
              onClick={() => handleNavigation('/terms-of-service')}
              sx={{ display: { xs: 'none', xl: 'inline-flex' } }}
            >
              Terms
            </Button>
            <Button
              color="primary"
              size="small"
              variant="text"
              component={RouterLink}
              to="/login"
            >
              Sign in
            </Button>
            <Button
              color="primary"
              size="small"
              variant="outlined"
              component={RouterLink}
              to={publicRegisterHref}
              sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
            >
              Create account
            </Button>
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            ml: isRegistered ? { xs: 'auto', lg: 0 } : 0,
          }}
        >
          {isRegistered && (
            <>
              <Tooltip title="Quick actions">
                <IconButton
                  aria-label="quick actions"
                  onClick={(event) => setQuickMenuAnchor(event.currentTarget)}
                  sx={{ color: 'text.primary' }}
                >
                  <QuickActionsIcon />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={quickMenuAnchor}
                open={Boolean(quickMenuAnchor)}
                onClose={() => setQuickMenuAnchor(null)}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                {quickActions.map((action) => (
                  <MenuItem key={action.path} onClick={() => handleNavigation(action.path)}>
                    <ListItemIcon>{action.icon}</ListItemIcon>
                    <ListItemText>{action.label}</ListItemText>
                  </MenuItem>
                ))}
              </Menu>

              <Tooltip title="Notifications">
                <IconButton
                  aria-label="notifications"
                  onClick={(event) => setNotificationsAnchor(event.currentTarget)}
                  sx={{ color: 'text.primary' }}
                >
                  <Badge badgeContent={workspace.unreadCount} color="error" max={9}>
                    <NotificationsIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={notificationsAnchor}
                open={Boolean(notificationsAnchor)}
                onClose={() => setNotificationsAnchor(null)}
                PaperProps={{
                  sx: {
                    minWidth: { xs: 'min(320px, calc(100vw - 32px))', sm: 320 },
                    maxWidth: 400,
                    maxHeight: 360,
                    overflowY: 'auto',
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                  },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                {workspace.notifications.length === 0 ? (
                  <MenuItem disabled>
                    <ListItemText primary="No new notifications" secondary="You're up to date." />
                  </MenuItem>
                ) : (
                  workspace.notifications.map((item) => (
                    <MenuItem
                      key={item.id}
                      onClick={() => {
                        handleNavigation(item.path);
                        workspace.dismissNotification(item.id);
                      }}
                    >
                      <ListItemText
                        primary={item.title}
                        secondary={item.description}
                        primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem', color: 'text.primary' }}
                        secondaryTypographyProps={{ fontSize: '0.75rem', color: 'text.secondary' }}
                      />
                    </MenuItem>
                  ))
                )}
              </Menu>
            </>
          )}

          <FormControlLabel
            sx={{
              mr: 1,
              ml: 0,
              color: 'text.secondary',
              display: { xs: 'none', sm: 'flex' },
            }}
            control={(
              <Switch
                checked={isDarkMode}
                onChange={toggleThemeMode}
                inputProps={{ 'aria-label': 'toggle dark mode' }}
                color="primary"
              />
            )}
            label={isDarkMode ? <DarkModeIcon fontSize="small" /> : <LightModeIcon fontSize="small" />}
            labelPlacement="start"
          />
          <IconButton
            size="large"
            aria-label="open user menu"
            onClick={handleMenuOpen}
            sx={{
              color: 'text.primary',
              '&:hover': {
                backgroundColor: mode === 'light' ? 'rgba(15, 23, 42, 0.06)' : 'rgba(255, 255, 255, 0.08)',
              },
            }}
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
              <AccountIcon />
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: {
                mt: 1,
                borderRadius: 2,
                minWidth: { xs: 'min(240px, calc(100vw - 32px))', sm: 240 },
                maxWidth: 'calc(100vw - 16px)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            {isRegistered && (companyName || registrationData?.email) && (
              <>
                <Box sx={{ px: 2, py: 1.25 }}>
                  {companyName && (
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {companyName}
                    </Typography>
                  )}
                  {registrationData?.email && (
                    <Typography variant="caption" color="text.secondary">
                      {registrationData.email}
                    </Typography>
                  )}
                  {(workspace.carbonScore !== null && workspace.carbonScore > 0) || workspace.totalTCO2e !== null ? (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {workspace.carbonScore !== null && workspace.carbonScore > 0
                        ? `Carbon score ${workspace.carbonScore}`
                        : null}
                      {workspace.carbonScore !== null && workspace.carbonScore > 0 && workspace.totalTCO2e !== null
                        ? ' · '
                        : null}
                      {workspace.totalTCO2e !== null ? `${workspace.totalTCO2e.toFixed(1)} tCO₂e` : null}
                    </Typography>
                  ) : null}
                  {workspace.apiOnline !== null && (
                    <Typography variant="caption" color={workspace.apiOnline ? 'success.main' : 'warning.main'} display="block">
                      {workspace.apiOnline ? 'Workspace connected' : 'Offline — data may be stale'}
                    </Typography>
                  )}
                </Box>
                <Divider />
              </>
            )}

            {isRegistered && (
              <MenuItem onClick={() => handleNavigation('/msme-profile')}>
                <ListItemIcon>
                  <BusinessIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Company profile</ListItemText>
              </MenuItem>
            )}

            {isRegistered && (
              <MenuItem onClick={() => handleNavigation('/settings')}>
                <ListItemIcon>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Settings</ListItemText>
              </MenuItem>
            )}

            {isRegistered && hasPortalAccess && (
              <MenuItem onClick={() => handleNavigation('/admin/dashboard')}>
                <ListItemIcon>
                  <AdminIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{isReviewer && !isAdmin ? 'Reviewer portal' : 'Admin portal'}</ListItemText>
              </MenuItem>
            )}

            {isRegistered && isSupport && (
              <MenuItem onClick={() => handleNavigation('/support/dashboard')}>
                <ListItemIcon>
                  <HelpIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Support portal</ListItemText>
              </MenuItem>
            )}

            {isRegistered && isExpert && (
              <MenuItem onClick={() => handleNavigation('/expert/dashboard')}>
                <ListItemIcon>
                  <AdminIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Expert review</ListItemText>
              </MenuItem>
            )}

            {isRegistered && isPartner && (
              <MenuItem onClick={() => handleNavigation('/partner/dashboard')}>
                <ListItemIcon>
                  <PartnersIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Partner portal</ListItemText>
              </MenuItem>
            )}

            {isRegistered && (
              <>
                <MenuItem onClick={handleAboutUsNavigation}>
                  <ListItemIcon>
                    <AboutInfoIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>About us</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleNavigation('/why-carbon-intelligence')}>
                  <ListItemIcon>
                    <WhyIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Why Carbon Intelligence?</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleNavigation('/insights')}>
                  <ListItemIcon>
                    <InsightsIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Insights &amp; articles</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleNavigation('/partners')}>
                  <ListItemIcon>
                    <PartnersIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Partners &amp; APIs</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleNavigation('/help')}>
                  <ListItemIcon>
                    <HelpIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Help center</ListItemText>
                </MenuItem>
              </>
            )}

            <MenuItem onClick={toggleThemeMode}>
              <ListItemIcon>
                {isDarkMode ? <DarkModeIcon fontSize="small" /> : <LightModeIcon fontSize="small" />}
              </ListItemIcon>
              <ListItemText>{isDarkMode ? 'Dark Mode' : 'Light Mode'}</ListItemText>
            </MenuItem>

            <Divider />

            {isRegistered && (
              <>
                <MenuItem onClick={() => handleNavigation('/data-privacy')}>
                  <ListItemIcon>
                    <PrivacyIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Data privacy</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleNavigation('/terms-of-service')}>
                  <ListItemIcon>
                    <TermsIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Terms of service</ListItemText>
                </MenuItem>
                <Divider />
              </>
            )}

            {!isRegistered && (
              <>
                <MenuItem onClick={handleAboutUsNavigation}>
                  <ListItemIcon>
                    <AboutInfoIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>About us</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleNavigation('/why-carbon-intelligence')}>
                  <ListItemIcon>
                    <WhyIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Why Carbon Intelligence?</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleNavigation('/pricing')}>
                  <ListItemIcon>
                    <PricingIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Pricing</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleNavigation('/insights')}>
                  <ListItemIcon>
                    <InsightsIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Insights &amp; articles</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleNavigation('/partners')}>
                  <ListItemIcon>
                    <PartnersIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Partners &amp; APIs</ListItemText>
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => handleNavigation('/forgot-password')}>
                  <ListItemIcon>
                    <SettingsIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Forgot Password</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleNavigation('/help')}>
                  <ListItemIcon>
                    <HelpIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Help center</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleNavigation('/privacy-policy')}>
                  <ListItemIcon>
                    <PrivacyIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Privacy</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleNavigation('/terms-of-service')}>
                  <ListItemIcon>
                    <TermsIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Terms</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleNavigation(publicRegisterHref)}>
                  <ListItemIcon>
                    <RegisterIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Create Account</ListItemText>
                </MenuItem>
                <Divider />
              </>
            )}

            <MenuItem onClick={handleAuthAction}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                {isRegistered ? 'Logout' : 'Sign in'}
              </ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>

      {showBreadcrumbs && (
        <Box
          sx={{
            ...headerMainColumnInsetSx(showBreadcrumbs, desktopSidebarCollapsed),
            pb: 0.5,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          {isDesktopNav && desktopSidebarCollapsed && onExpandDesktopSidebar ? (
            <Tooltip title="Expand sidebar">
              <IconButton
                size="small"
                aria-label="Expand sidebar"
                onClick={onExpandDesktopSidebar}
                sx={{ color: 'text.primary', flexShrink: 0 }}
              >
                <MenuOpenIcon />
              </IconButton>
            </Tooltip>
          ) : null}
          <HeaderBreadcrumbs />
        </Box>
      )}
    </AppBar>
  );
};

export default Header;
